import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL } from '../config';

// Public API endpoints
const PUBLIC_API = `${API_BASE_URL}/public`;

interface SponsorshipFormData {
  company_name: string;
  contact_name: string;
  email: string;
  phone: string;
  tier: 'gold' | 'silver' | 'bronze';
  website: string;
  message: string;
  is_lt_innovate_member: boolean;
  member_type: 'regular' | 'premium' | '';
}

interface SubmitResponse {
  success: boolean;
  message: string;
}

const SponsorshipForm: React.FC = () => {
  const [formData, setFormData] = useState<SponsorshipFormData>({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    tier: 'gold',
    website: '',
    message: '',
    is_lt_innovate_member: false,
    member_type: ''
  });

  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Query conference settings to check if sponsorships are open
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['conferenceSettings'],
    queryFn: async () => {
      const response = await axios.get(`${PUBLIC_API}/conference-settings`);
      return response.data;
    }
  });

  // Mutation for submitting the form
  const submitMutation = useMutation<SubmitResponse, Error, SponsorshipFormData>({
    mutationFn: async (data: SponsorshipFormData) => {
      const response = await axios.post(`${PUBLIC_API}/sponsorship/apply`, data);
      return response.data;
    },
    onSuccess: () => {
      setSubmitSuccess(true);
      setSubmitError(null);
      // Reset form
      setFormData({
        company_name: '',
        contact_name: '',
        email: '',
        phone: '',
        tier: 'gold',
        website: '',
        message: '',
        is_lt_innovate_member: false,
        member_type: ''
      });
    },
    onError: (error: any) => {
      setSubmitSuccess(false);
      setSubmitError(error.response?.data?.detail || 'An error occurred while submitting your application.');
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    submitMutation.mutate(formData);
  };

  if (settingsLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!settings?.accepting_sponsors) {
    return (
      <div className="max-w-2xl mx-auto p-6 bg-gray-50 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Become a Sponsor</h2>
        <p className="text-gray-600">
          We are currently not accepting new sponsorship applications. Please contact us at{' '}
          <a href="mailto:summit@lt-innovate.org" className="text-blue-600 hover:underline">
            summit@lt-innovate.org
          </a>{' '}
          for more information about future sponsorship opportunities.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Sponsorship Form */}
      <div className="p-6 bg-white rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Sponsorship Application</h2>

        {submitSuccess && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
            <p className="text-green-800">
              Thank you for your interest in sponsoring our conference! We will review your application
              and contact you soon to discuss the next steps.
            </p>
          </div>
        )}

        {submitError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800">{submitError}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
              Company Name *
            </label>
            <input
              type="text"
              id="companyName"
              required
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-1">
              Contact Person *
            </label>
            <input
              type="text"
              id="contactName"
              required
              value={formData.contact_name}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                id="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
              Company Website
            </label>
            <input
              type="url"
              id="website"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="tier" className="block text-sm font-medium text-gray-700 mb-1">
              Preferred Sponsorship Tier *
            </label>
            <select
              id="tier"
              required
              value={formData.tier}
              onChange={(e) => setFormData({ ...formData, tier: e.target.value as SponsorshipFormData['tier'] })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="gold">Gold Sponsor (€6500)</option>
              <option value="silver">Silver Sponsor (€5300)</option>
              <option value="bronze">Bronze Sponsor (€2530)</option>
            </select>
          </div>

          <div className="mb-4">
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="ltInnovateMember"
                checked={formData.is_lt_innovate_member}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setFormData({
                    ...formData,
                    is_lt_innovate_member: isChecked,
                    // Reset member_type if unchecked
                    member_type: isChecked ? formData.member_type : ''
                  });
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="ltInnovateMember" className="ml-2 block text-sm text-gray-700">
                I am an LT-INNOVATE member
              </label>
            </div>

            {formData.is_lt_innovate_member && (
              <div className="ml-6 mt-2">
                <label htmlFor="memberType" className="block text-sm font-medium text-gray-700 mb-1">
                  Membership Type *
                </label>
                <select
                  id="memberType"
                  required={formData.is_lt_innovate_member}
                  value={formData.member_type}
                  onChange={(e) => setFormData({ ...formData, member_type: e.target.value as 'regular' | 'premium' | '' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select membership type</option>
                  <option value="regular">LT-INNOVATE REGULAR</option>
                  <option value="premium">LT-INNOVATE PREMIUM</option>
                </select>
              </div>
            )}
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
              Additional Information
            </label>
            <textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Please share any specific requirements or questions you may have"
            />
          </div>

          <div>
            <button
              type="submit"
              disabled={submitMutation.isPending}
              className={`w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors ${
                submitMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {submitMutation.isPending ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SponsorshipForm;
